import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import { compose } from 'redux';
import { injectIntl } from 'react-intl';
import cx from 'classnames';
import queryString from 'query-string';
import history from '../../history';
import { productClickEvent } from '../../helpers/googleTagManager';
import withResponsiveWrapper from '../../decorators/withResponsiveWrapper';
import { SHOP_PATH, PER_PAGE, COMPONENT_TYPES } from '../../constants';
import {
  shopMessages as messages,
  filtersMessages,
} from '../../defineMessages';
import Label from '../Label';
import Link from '../Link';
import Grid from '../Grid';
import TopBar from './components/TopBar';
import Sidebar from './components/Sidebar';
import Banner from './components/Banner';
import ProductList from './components/ProductList/ProductList';
// eslint-disable-next-line css-modules/no-unused-class
import s from './ProductListPage.scss';
import { hasBothTypesProducts } from '../../helpers';

const LoadingProducts = ({ length }) => {
  const data = [...Array(length).keys()];
  return data.map(index => (
    <Grid item xs={12} sm={8} md={6} key={`dummy-${index}`}>
      <div className={s.dummy} />
    </Grid>
  ));
};

const ProductListPage = ({
  data,
  filters,
  currency,
  ordering,
  sortBy,
  loading,
  totalItems,
  isIntersecting,
  isDesktop,
  onFilterChange,
  onSortBy,
  intl: { formatMessage },
  banner,
  mode,
  nextPage,
  salesChannelId,
  catalogMode,
  className,
  isClient,
  presentations,
}) => {
  const [pageScrolledDown, setPagedScrolledDown] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      if (!isDesktop) {
        const pageOffset = window.scrollY || document.documentElement.scrollTop;
        const offsetSize = banner ? 150 : 40;
        if (pageOffset <= offsetSize) {
          setPagedScrolledDown(false);
        } else {
          setPagedScrolledDown(true);
        }
      }
    };
    document.addEventListener('scroll', onScroll, false);

    // Cleanup
    return () => {
      document.removeEventListener('scroll', onScroll, false);
    };
  }, [banner, isDesktop]);

  const translatedTypeFilters = Object.assign(
    {},
    ...Object.keys(filters.type).map(key => ({
      [key]: {
        ...filters.type[key],
        label: formatMessage({ ...filtersMessages[key] }),
      },
    })),
  );

  const showPriceInterval = filters.type.physicalProduct?.checked || false;

  // Hold filters in an array instead
  let mutatedFilters = Object.keys(filters).reduce((res, filterKey) => {
    const filterValue = filters[filterKey];
    res.push({
      type: 'filter',
      key: filterKey,
      items: filterValue,
    });
    return res;
  }, []);

  // Use translated versions of the type filter
  mutatedFilters.find(
    filter => filter.key === 'type',
  ).items = translatedTypeFilters;

  // Remove all filters without items
  mutatedFilters = mutatedFilters.filter(
    filter => Object.keys(filter.items).length > 0,
  );

  // If used, insert the price interval filter at index 1 (after the type filter)
  if (showPriceInterval) {
    mutatedFilters.splice(1, 0, {
      type: 'priceInterval',
      key: 'priceInterval',
    });
  }

  const sortingOptions = ordering.map(({ value, label }) => ({
    value,
    label: formatMessage({ ...messages[label] }),
  }));

  const { search } = history.location || {};
  const parsedSearch = queryString.parse(search);
  parsedSearch.page = nextPage;
  const stringifiedSearch = queryString.stringify(parsedSearch);

  const checkedSorting = sortBy || parsedSearch.order;

  const gtmProductClickEvent = (id, productTitle, price, index) => {
    if (catalogMode) return;
    productClickEvent({
      eventData: {
        currencyCode: currency,
        click: {
          actionField: {
            list: 'shop',
            salesChannelId,
            mode,
          },
          products: [
            {
              id,
              name: productTitle,
              price,
              position: index + 1,
            },
          ],
        },
      },
    });
  };

  // Remove UI filter for product types if only 1 type present: giftcard or physical
  mutatedFilters =
    parsedSearch.type || hasBothTypesProducts(data)
      ? mutatedFilters
      : mutatedFilters.filter(filter => filter.key !== 'type');
  return (
    <div className={cx(s.root, !isIntersecting && s.isSticky, className)}>
      <div className={s.container}>
        {banner && (
          <div className={cx(s.banner, pageScrolledDown && s.hidden)}>
            <Banner banner={banner} />
          </div>
        )}
        {presentations?.components
          ?.filter(c => c.templateType === COMPONENT_TYPES.BANNER)
          .map(comp => (
            <div
              key={comp.id}
              dangerouslySetInnerHTML={{ __html: comp.localizedContent }}
            />
          ))}
        {
          <>
            {isClient &&
            isDesktop && ( // only render on client to avoid layout shifts
                <Sidebar
                  isSticky={!isIntersecting}
                  filterProps={{
                    filters: mutatedFilters,
                    onFilterChange,
                  }}
                  mode={mode}
                />
              )}
            {isClient && ( // only render on client to avoid layout shifts
              <TopBar
                className={cx(
                  s.topBar,
                  catalogMode && s.catalogMode,
                  !isIntersecting && !banner && s.isSticky,
                  !isDesktop && pageScrolledDown && s.fixed,
                  !isDesktop && banner && s.withBanner,
                )}
                sortByProps={{
                  sortBy: checkedSorting,
                  options: sortingOptions,
                  onSortBy,
                  label: formatMessage({ ...messages.sortBy }),
                }}
                filterProps={{
                  filters: mutatedFilters,
                  onFilterChange,
                }}
                isDesktop={isDesktop}
              />
            )}
          </>
        }
        {data.length > 0 || loading ? (
          <>
            <ProductList
              products={data}
              isDesktop={isDesktop}
              onProductClick={gtmProductClickEvent}
              openProductInModal={catalogMode}
            />
            {loading && <LoadingProducts length={12} />}
          </>
        ) : (
          <Label>{formatMessage({ ...messages.noProducts })}</Label>
        )}
      </div>

      {totalItems > PER_PAGE && (
        <Link
          to={`${SHOP_PATH}?${stringifiedSearch}`}
          style={{ display: 'none' }}
        />
      )}
    </div>
  );
};

ProductListPage.propTypes = {
  ordering: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  isIntersecting: PropTypes.bool.isRequired,
  isDesktop: PropTypes.bool.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  nextPage: PropTypes.number.isRequired,
  filters: PropTypes.shape().isRequired,
  sortBy: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  totalItems: PropTypes.number.isRequired,
  intl: PropTypes.shape().isRequired,
  currency: PropTypes.string.isRequired,
  onFilterChange: PropTypes.func,
  className: PropTypes.string,
  onSortBy: PropTypes.func,
  mode: PropTypes.string.isRequired,
  salesChannelId: PropTypes.string.isRequired,
  isClient: PropTypes.bool.isRequired,
  catalogMode: PropTypes.bool,
  banner: PropTypes.arrayOf(
    PropTypes.shape({
      desktop: PropTypes.string,
      mobile: PropTypes.string,
      alt: PropTypes.string,
      id: PropTypes.number,
      link: PropTypes.string,
    }),
  ),
  presentations: PropTypes.shape(),
};

ProductListPage.defaultProps = {
  sortBy: '',
  onSortBy: null,
  className: '',
  banner: [
    {
      desktop: undefined,
      mobile: undefined,
      alt: 'Shop',
      id: undefined,
      link: '',
    },
  ],
  catalogMode: false,
  onFilterChange: null,
  presentations: null,
};

export default compose(
  injectIntl,
  withStyles(s),
  withResponsiveWrapper(),
)(ProductListPage);
